import React from 'react';
import { Button, Space, Tooltip, Modal, message } from 'antd';
import { EyeOutlined, EditOutlined, CloseCircleOutlined, DollarOutlined, ExclamationCircleOutlined, WarningOutlined } from '@ant-design/icons';

const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

const RISKY_STATUSES = ['RECIBIDO', 'EN_TRANSITO', 'PARCIAL'];

const STATUS_EDIT_COLOR = {
    PENDIENTE: '#1890ff',
    APROBADO: '#1890ff',
    RECIBIDO: '#fa8c16',
    EN_TRANSITO: '#fa8c16',
    PARCIAL: '#fa8c16',
};

const STATUS_EDIT_LABEL = {
    PENDIENTE: 'Pendiente',
    APROBADO: 'Aprobado',
    RECIBIDO: 'Recibido',
    EN_TRANSITO: 'En Tránsito',
    PARCIAL: 'Parcial',
};

const buildEditWarningMessage = (order) => {
    const label = STATUS_EDIT_LABEL[order.status] || order.status;
    return (
        <div>
            <p>
                Esta orden está en estado <strong>{label}</strong>. Editar los ítems puede
                alterar stock, pagos y/o cuenta corriente ya registrados, y requerirá
                reconciliación manual.
            </p>
            <p>¿Confirma que desea continuar con la edición?</p>
        </div>
    );
};

const buildCancelWarningMessage = (order) => {
    const label = STATUS_EDIT_LABEL[order.status] || order.status;
    return (
        <div>
            <p>
                Esta orden está en estado <strong>{label}</strong>. Cancelarla generará una
                transacción reversa en Caja y/o cuenta corriente, ajustará el stock y
                quedará registrada en la auditoría con motivo de cancelación.
            </p>
            <p>Esta acción <strong>no se puede deshacer</strong>. ¿Cancelar la orden?</p>
        </div>
    );
};

/**
 * Reusable actions cell for an order row (used by the main orders table
 * and by the table inside the "Órdenes por Proveedor" modal).
 *
 * Props:
 * - order:           order record (must include id, status, order_number, ...)
 * - isAdmin:         boolean — current user is ClientAdministrator / admin
 * - canEditOrder(status): pure function returning true when status is editable
 * - getStatusLabel(status): pure function returning localized status label
 * - onView(order):   callback to open order details
 * - onEdit(order):   callback to open the edit-items modal
 * - onCancel(order): callback to confirm & cancel the order
 * - onRegisterPayment(order): callback to open the payment modal
 */
const OrderRowActions = ({
    order,
    isAdmin,
    canEditOrder,
    getStatusLabel,
    onView,
    onEdit,
    onCancel,
    onRegisterPayment,
}) => {
    const canEdit = canEditOrder(order.status);
    const editDisabledReason = !canEdit
        ? `No se puede editar (estado: ${getStatusLabel(order.status)})`
        : !isAdmin
            ? 'Requiere rol ClientAdministrator'
            : null;

    const editColor = STATUS_EDIT_COLOR[order.status] || '#1890ff';
    const isRisky = RISKY_STATUSES.includes(order.status);

    const handleEditClick = () => {
        if (isDemoMode) {
            message.info('Editar ítems de la orden — disponible en la versión completa del sistema');
            return;
        }
        if (isRisky) {
            Modal.confirm({
                title: 'Editar orden ya Recibida/En Tránsito/Parcial',
                icon: <WarningOutlined style={{ color: '#fa8c16' }} />,
                content: buildEditWarningMessage(order),
                okText: 'Sí, editar',
                okType: 'primary',
                cancelText: 'No, cancelar',
                onOk: () => onEdit(order),
            });
        } else {
            onEdit(order);
        }
    };

    const handleCancelClick = () => {
        if (isDemoMode) {
            message.info('Cancelar orden — disponible en la versión completa del sistema');
            return;
        }
        if (isRisky) {
            Modal.confirm({
                title: 'Cancelar orden ya Recibida/En Tránsito/Parcial',
                icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
                content: buildCancelWarningMessage(order),
                okText: 'Sí, cancelar orden',
                okType: 'danger',
                cancelText: 'No',
                onOk: () => onCancel(order),
            });
        } else {
            onCancel(order);
        }
    };

    return (
        <Space size="small">
            <Button
                type="primary"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => {
                    if (isDemoMode) {
                        message.info('Vista de detalles de la orden de compra');
                        return;
                    }
                    onView(order);
                }}
                title="Ver detalles"
            />
            <Tooltip title={isDemoMode ? null : (editDisabledReason || (isRisky ? 'Editar items (orden ya Recibida/En Tránsito/Parcial — se pedirá confirmación)' : 'Editar items'))}>
                <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={handleEditClick}
                    disabled={!isDemoMode && (!canEdit || !isAdmin)}
                    style={{
                        backgroundColor: canEdit && isAdmin ? editColor : undefined,
                        borderColor: canEdit && isAdmin ? editColor : undefined,
                        color: canEdit && isAdmin ? 'white' : undefined,
                    }}
                />
            </Tooltip>
            <Button
                danger
                size="small"
                icon={<CloseCircleOutlined />}
                onClick={handleCancelClick}
                title="Cancelar orden"
                disabled={!isDemoMode && order.status === 'CANCELADO'}
            />
            {isAdmin && order.status !== 'CANCELADO' && (
                <Tooltip title="Registrar pago">
                    <Button
                        size="small"
                        icon={<DollarOutlined />}
                        onClick={() => {
                            if (isDemoMode) {
                                message.info('Registrar pago — disponible en la versión completa del sistema');
                                return;
                            }
                            onRegisterPayment(order);
                        }}
                        style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', color: 'white' }}
                    />
                </Tooltip>
            )}
        </Space>
    );
};

export default OrderRowActions;
